import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { usePositionsStream, usePriceFeed } from '../hooks/useWebSocket';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, TrendingUp, TrendingDown, Activity, DollarSign, Wifi, WifiOff, Zap } from 'lucide-react';

const Positions = () => {
  const { user } = useAuth();
  const tenantId = user?.tenant_id || 'default';
  
  // WebSocket real-time data
  const { positions: wsPositions, priceTicks, isConnected, error: wsError } = usePositionsStream(tenantId);
  const { prices: livePrices } = usePriceFeed();
  
  // Fallback to REST API if WebSocket fails
  const [fallbackPositions, setFallbackPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const positions = wsPositions.length > 0 ? wsPositions : fallbackPositions;

  const fetchPositions = async () => {
    try {
      const response = await api.getPositions();
      setFallbackPositions(response.data);
    } catch (error) {
      toast.error('Failed to load positions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Initial load via REST
    fetchPositions();
    
    // If WebSocket fails, poll every 5 seconds
    if (!isConnected) {
      const interval = setInterval(fetchPositions, 5000);
      return () => clearInterval(interval);
    }
  }, [isConnected]);

  useEffect(() => {
    if (wsPositions.length > 0) {
      setLoading(false);
    }
  }, [wsPositions]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPositions();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPrice = (value, symbol) => {
    const decimals = symbol.includes('JPY') ? 3 : symbol.includes('XAU') || symbol.includes('BTC') ? 2 : 5;
    return value.toFixed(decimals);
  };

  // Calculate summary stats
  const totalProfit = positions.reduce((sum, p) => sum + p.profit, 0);
  const buyPositions = positions.filter(p => p.type === 'BUY').length;
  const sellPositions = positions.filter(p => p.type === 'SELL').length;

  return (
    <div className="space-y-6" data-testid="positions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Open Positions</h1>
          <p className="text-muted-foreground">Real-time MT5 position monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          {/* WebSocket Status */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            isConnected ? 'bg-emerald-500/10' : 'bg-amber-500/10'
          }`}>
            {isConnected ? (
              <>
                <Wifi className="w-4 h-4 text-emerald-400" />
                <Zap className="w-3 h-3 text-emerald-400 animate-pulse" />
                <span className="text-xs text-emerald-400">Live Stream</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-amber-400">Polling Mode</span>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-positions-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Live Price Ticker */}
      {Object.keys(livePrices).length > 0 && (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex items-center gap-6 px-4 py-3 overflow-x-auto animate-fade-in-up">
              <span className="text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                Live Prices
              </span>
              {Object.entries(livePrices).map(([symbol, data]) => (
                <div key={symbol} className="flex items-center gap-2 whitespace-nowrap">
                  <span className="font-medium text-sm">{symbol}</span>
                  <span className="font-mono text-sm">{formatPrice(data.bid, symbol)}</span>
                  <span className={`text-xs ${data.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {data.change >= 0 ? '+' : ''}{data.change}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="animate-fade-in-up">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Positions</p>
              <p className="text-xl font-bold font-mono">{positions.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Buy Positions</p>
              <p className="text-xl font-bold font-mono text-emerald-400">{buyPositions}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-500/10">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Sell Positions</p>
              <p className="text-xl font-bold font-mono text-red-400">{sellPositions}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-300">
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${totalProfit >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              <DollarSign className={`w-5 h-5 ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total P&L</p>
              <p className={`text-xl font-bold font-mono ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {totalProfit >= 0 ? '+' : ''}{formatCurrency(totalProfit)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Positions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Live Positions
            {isConnected && (
              <Badge variant="outline" className="ml-2 text-emerald-400 border-emerald-400/30">
                <span className="w-2 h-2 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />
                Real-time
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : positions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No open positions</p>
              <p className="text-sm">Positions will appear here when trades are opened</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Volume</TableHead>
                    <TableHead>Open Price</TableHead>
                    <TableHead>Current Price</TableHead>
                    <TableHead>Profit</TableHead>
                    <TableHead>Open Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.map((position, index) => {
                    // Get live price if available
                    const livePrice = livePrices[position.symbol]?.bid || position.current_price;
                    const priceTick = priceTicks[position.symbol];
                    
                    return (
                      <TableRow 
                        key={position.id || index} 
                        data-testid={`position-row-${position.id || index}`}
                        className="transition-colors duration-300"
                      >
                        <TableCell className="font-mono text-muted-foreground">
                          #{position.mt5_ticket}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{position.symbol}</span>
                            {priceTick && (
                              <span className={`text-xs ${priceTick.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {priceTick.change >= 0 ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={position.type === 'BUY' 
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }>
                            {position.type === 'BUY' ? (
                              <TrendingUp className="w-3 h-3 mr-1" />
                            ) : (
                              <TrendingDown className="w-3 h-3 mr-1" />
                            )}
                            {position.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{position.volume.toFixed(2)}</TableCell>
                        <TableCell className="font-mono">{formatPrice(position.open_price, position.symbol)}</TableCell>
                        <TableCell className="font-mono">
                          <span className={priceTick ? 'animate-pulse' : ''}>
                            {formatPrice(livePrice, position.symbol)}
                          </span>
                        </TableCell>
                        <TableCell className={`font-mono font-medium ${
                          position.profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {position.profit >= 0 ? '+' : ''}{formatCurrency(position.profit)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {new Date(position.open_time).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Positions;
